import React from 'react';
import styled, { css } from 'styled-components';

import { FormattedDateFormat, formatDateTime, getFormattedDate, isValidDate } from '~/util';

interface RelativeTimeProps {
  className?: string;
  timestamp: Date | string;
  format?: FormattedDateFormat;
  preventHover?: boolean;
  alignment?: 'left' | 'right';
}

const Relative = styled.div<{ alignment?: 'left' | 'right' }>`
  display: inline-block;
  position: relative;
  opacity: 1;
  white-space: nowrap;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s;
  pointer-events: auto;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
      position: absolute;
    `}
`;

export const Absolute = styled.div<{ alignment?: 'left' | 'right' }>`
  position: absolute;
  display: inline-block;
  pointer-events: none;
  isolation: isolate;
  top: 0;
  right: 0;
  white-space: nowrap;
  opacity: 0;
  z-index: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  transition: opacity 0.2s;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
    `}

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      background-color: ${theme.secondary.blendToBackground(25)};
    `}

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 32px;
    z-index: 2;
    pointer-events: none;
  }

  &:before {
    right: 100%;
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) =>
          theme.id !== 'default' ? theme.secondary.blendToBackground(25) : theme.secondary.blendToBackground(50)}
        100%
    );
  }

  &:after {
    left: 100%;
    background-image: linear-gradient(
      90deg,
      ${({ theme }) =>
          theme.id !== 'default' ? theme.secondary.blendToBackground(25) : theme.secondary.blendToBackground(50)}
        0%,
      transparent 100%
    );
  }
`;

const Wrapper = styled.div<{ preventHover?: boolean; alignment?: 'left' | 'right' }>`
  position: relative;
  font-size: 12px;
  line-height: 16px;
  height: 16px;
  display: inline-block;
  width: fit-content;

  ${({ preventHover }) =>
    !preventHover &&
    css`
      ${Relative}:hover ~ ${Absolute}, ${Absolute}:hover {
        opacity: 1;
        width: max-content;
        overflow: visible;
        padding: 0 0 0 12px;
        pointer-events: auto;
      }

      ${Relative}:hover {
        opacity: 0;
      }
    `}

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      ${Relative}:hover ~ ${Absolute}, ${Absolute}:hover {
        padding: 0 12px 0 0;
      }
    `};
`;

export const RelativeTime: React.FC<RelativeTimeProps> = ({
  className,
  timestamp,
  format = 'time-passed',
  preventHover = false,
  alignment = 'right',
}) => {
  return (
    <Wrapper className={className} preventHover={preventHover} alignment={alignment}>
      {!isValidDate(timestamp) ? (
        '-'
      ) : (
        <>
          <Relative alignment={alignment}>{getFormattedDate(timestamp, format)}</Relative>
          <Absolute alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'local' })}</Absolute>
        </>
      )}
    </Wrapper>
  );
};
